import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import MemoryIcon from '@mui/icons-material/Memory';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import SchemaIcon from '@mui/icons-material/Schema';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import DesignServicesIcon from '@mui/icons-material/DesignServices';

const SemiconductorServices: React.FC = () => {
  const services = [
    {
      title: 'Semiconductor Process Consulting',
      description:
        'Optimization of wafer fabrication processes, yield improvement strategies, SPC/Six Sigma deployment, and continuous improvement frameworks.',
      icon: <PrecisionManufacturingIcon fontSize="large" />,
    },
    {
      title: 'Design Enablement and EDA Flows',
      description:
        'Design methodology audits, foundry PDK onboarding, DFM/DFY checks, and automated CI/CD for RTL-to-GDS flows.',
      icon: <SchemaIcon fontSize="large" />,
    },
    {
      title: 'Verification and Validation',
      description:
        'Functional verification, UVM-based environments, mixed-signal co-sim, and post-silicon bring-up and validation planning.',
      icon: <MemoryIcon fontSize="large" />,
    },
    {
      title: 'Semiconductor Training & Capability Building',
      description:
        'Role-based training tracks across design, verification, DFT, STA, physical design, and manufacturing (DOE, SPC, FMEA) with hands-on labs and tool enablement.',
      icon: <SchoolIcon fontSize="large" />,
    },
    {
      title: 'Product Development & IC/SoC Design',
      description:
        'Spec-to-silicon services: architecture, RTL, synthesis, PnR, timing closure, sign-off (LVS/DRC), IP integration, firmware, and FPGA prototyping.',
      icon: <DesignServicesIcon fontSize="large" />,
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              Semiconductor Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Consulting and engineering services for fab, design, verification, and manufacturing excellence
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h4" component="h2" fontWeight="bold" color="#0c2340" textAlign="center" sx={{ mb: 6 }}>
          What We Offer
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
          {services.map((service, index) => (
            <Box key={index} sx={{ width: { xs: '100%', md: '50%' }, px: 2, mb: 4 }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}>
                <Paper elevation={0} sx={{ p: 5, height: '100%', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
                  <Box sx={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#1976d215', color: '#1976d2', mb: 3 }}>
                    {service.icon}
                  </Box>
                  <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom color="#0c2340">
                    {service.title}
                  </Typography>
                  <Typography paragraph color="text.secondary" sx={{ mb: 3 }}>
                    {service.description}
                  </Typography>
                </Paper>
              </motion.div>
            </Box>
          ))}
        </Box>

        <Paper elevation={0} sx={{ p: 5, borderRadius: '16px', mt: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom color="#0c2340">
                Training Programs
              </Typography>
              <List>
                {[
                  'Foundations: semiconductor physics, FEOL/BEOL, yield, SPC and quality basics',
                  'Design tracks: RTL/UVM, DFT/ATPG, STA and timing, physical design (PnR/CTS)',
                  'Analog/Mixed-Signal: circuit design, verification, modeling, and co-simulation',
                  'Manufacturing excellence: DOE, FMEA, 8D/5-Why, MSA, metrology & reliability',
                  'EDA enablement: Synopsys, Cadence, Siemens flows and PDK onboarding',
                  'Delivery formats: instructor-led, hands-on labs, capstone projects, onsite/remote',
                ].map((point, i) => (
                  <ListItem key={`training-${i}`} disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={point} />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box sx={{ width: { xs: '100%', md: 420 }, flexShrink: 0 }}>
              <Box
                component="img"
                src="/sem1.jpeg"
                alt="Semiconductor cleanroom training"
                sx={{
                  width: '100%',
                  height: { xs: 200, md: 260 },
                  objectFit: 'contain',
                  borderRadius: '12px',
                }}
                loading="lazy"
              />
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: 5, borderRadius: '16px', mt: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom color="#0c2340">
                Design and Development Services
              </Typography>
              <List>
                {[
                  'Spec-to-silicon IC/SoC development: architecture, RTL, verification, synthesis, PnR',
                  'Timing closure, sign-off (LVS/DRC), power/performance/area optimization (PPA)',
                  'IP selection and integration, bus fabrics, clock/reset/power architecture',
                  'FPGA prototyping, board bring-up, and post-silicon validation planning',
                  'Firmware and driver development for evaluation boards and reference platforms',
                  'DFM/DFY reviews, test strategy, documentation, and release management',
                ].map((point, i) => (
                  <ListItem key={`dev-${i}`} disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={point} />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box sx={{ width: { xs: '100%', md: 420 }, flexShrink: 0 }}>
              <Box
                component="img"
                src="/sem2.jpeg"
                alt="Microchip and IC/SoC design close-up"
                sx={{
                  width: '100%',
                  height: { xs: 200, md: 260 },
                  objectFit: 'contain',
                  borderRadius: '12px',
                }}
                loading="lazy"
              />
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: 5, borderRadius: '16px', mt: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom color="#0c2340">
            Why Partner With Us
          </Typography>
          <List>
            {["Fab and design-side expertise","Proven frameworks for yield and quality","Tool-agnostic approach and rapid adoption","Secure, compliant delivery practices"].map((point, i) => (
              <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={point} />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 3 }} />
          <Button variant="outlined" endIcon={<ArrowForwardIcon />} component={Link} to="/contact?service=semiconductor" sx={{ borderRadius: '8px' }}>
            Contact Us
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default SemiconductorServices;


